import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Source = 'stedi_271' | 'portal' | 'phone' | 'human' | 'pms_existing';

export type VerificationStatus =
  | 'pending'
  | 'in_progress'
  | 'needs_review'
  | 'complete'
  | 'failed'
  | 'awaiting_production';

export type IntakeSource = 'open_dental_sync' | 'file_import' | 'manual';

export interface VerifiedField<T> {
  value: T | null;
  source: Source | null;
  confidence: number;
  verifiedAt: string | null;
  reviewed: boolean;
  note?: string;
}

export interface WorklistRow {
  id: string;
  patientName: string;
  patientDobMasked: string | null; // "••/••/1984" — the full DOB comes from revealDob()
  memberIdMasked: string;
  appointmentTypeLabel: string;
  appointmentAt: string | null;
  intakeSource: IntakeSource;
  carrierName: string;
  planType: string | null;
  groupName: string | null;
  status: VerificationStatus;
  failureCode: string | null;
  failureMessage: string | null;
  completeness: { met: number; total: number } | null;
  sources: Source[];
  reviewed: boolean;
  updatedAt: string;
}

export interface WorklistMetrics {
  verifiedThisWeek: number;
  upcomingThisWeek: number;
  awaitingReview: number;
  failed: number;
  awaitingProduction: number;
  hoursSavedThisMonth: number;
  cleanClaimReadinessPct: number;
}

export interface CategoryCoverage {
  category: string;
  coinsurancePercent: VerifiedField<number>;
  coverageLevel: string;
  deductibleApplies: VerifiedField<boolean>;
}

export interface Frequency {
  quantity: number;
  per: 'benefit_year' | 'calendar_year' | 'lifetime' | 'months';
  months: number | null;
}

export interface BenefitDetail {
  procedureCode: string;
  codeGroup: string | null;
  description: string;
  coinsurancePercent: VerifiedField<number>;
  frequency: VerifiedField<Frequency>;
  waitingPeriodMonths: VerifiedField<number>;
  ageLimit: VerifiedField<{ maxAge: number }>;
  downgrade: VerifiedField<{ downgradesToCode: string; note: string }>;
  isExcluded: VerifiedField<boolean>;
  missingToothClause: VerifiedField<boolean>;
  historyLastServiceDate: VerifiedField<string>;
}

export interface Verification {
  id: string;
  practiceId: string;
  locationId: string;
  patientId: string;
  appointmentId: string | null;
  appointmentTypeId: string;
  status: VerificationStatus;
  intakeSource: IntakeSource;
  importBatchId: string | null;
  createdAt: string;
  updatedAt: string;
  patient: { firstName: string; lastName: string; dateOfBirthMasked: string | null };
  appointment: { typeLabel: string; at: string | null; providerName: string | null };
  pms?: {
    system: 'open_dental';
    patNum: number;
    aptNum: number | null;
    patPlanNum: number | null;
    planNum: number | null;
    insSubNum: number | null;
  } | null;
  // Subscriber identity and group number stay server-side; only the masked
  // member ID and the relationship are sent to the browser.
  subscriber: {
    relationshipToPatient: 'self' | 'spouse' | 'child' | 'other';
    memberIdMasked: string;
  };
  plan: {
    carrierName: string;
    payerId: string | null;
    planType: VerifiedField<string>;
    groupName: string | null;
    effectiveDate: VerifiedField<string>;
    terminationDate: VerifiedField<string>;
    coverageStatus: VerifiedField<'active' | 'inactive' | 'termed' | 'unknown'>;
    networkStatus: VerifiedField<'in_network' | 'out_of_network' | 'tiered' | 'unknown'>;
  };
  maximums: {
    annualMaximum: VerifiedField<number>;
    annualMaximumUsed: VerifiedField<number>;
    annualMaximumRemaining: VerifiedField<number>;
    orthoLifetimeMaximum: VerifiedField<number>;
    orthoLifetimeUsed: VerifiedField<number>;
  };
  deductibles: {
    individualAnnual: VerifiedField<number>;
    individualMet: VerifiedField<number>;
    familyAnnual: VerifiedField<number>;
    familyMet: VerifiedField<number>;
    appliesToPreventive: VerifiedField<boolean>;
  };
  coordinationOfBenefits: {
    isPrimary: VerifiedField<boolean>;
    otherCarrierName: VerifiedField<string>;
    orderOfBenefits: VerifiedField<string>;
  };
  categories: CategoryCoverage[];
  benefitDetails: BenefitDetail[];
}

export interface UnmetRequired {
  id: string;
  label: string;
  reason: 'missing' | 'low_confidence' | 'unreviewed';
  tier: string | null;
  bestSource: string | null;
  confidence: number;
}

export interface UnmetOptional {
  id: string;
  label: string;
  reason: string;
  tier: string | null;
}

export interface CompletenessSummary {
  complete: boolean;
  metCount: number;
  total: number;
  unmetRequired: UnmetRequired[];
  unmetOptional: UnmetOptional[];
  evaluatedAt: string;
}

export type AuditEventType =
  | 'created'
  | 'import'
  | 'electronic_check'
  | 'electronic_failed'
  | 'awaiting_production'
  | 'review_approved'
  | 'review_flagged'
  | 'opendental_write'
  | 'opendental_dry_run'
  | 'call_queued'
  | 'phi_revealed';

export interface AuditEvent {
  id: string;
  type: AuditEventType;
  summary: string;
  payload: unknown;
  createdAt: string;
}

export interface RawResponseRef {
  id: string;
  kind: string;
  externalId: string | null;
  createdAt: string;
}

export interface BenefitWrite {
  sourceField: string;
  mode: 'dry_run' | 'live';
  benefitNum: number | null;
  payload: unknown;
  createdAt: string;
}

export interface VerificationDetail {
  verification: Verification;
  row: WorklistRow;
  completeness: CompletenessSummary | null;
  audit: AuditEvent[];
  rawResponses: RawResponseRef[];
  benefitWrites: BenefitWrite[];
}

export interface ApproveResult {
  status: string;
  mode: 'dry_run' | 'live' | 'none';
  written: {
    sourceField: string;
    mode: string;
    action: 'created' | 'updated' | 'unchanged';
    benefitNum: number | null;
    payload: unknown;
  }[];
  skipped: { sourceField: string; reason: string }[];
  heldBack: string[];
  stillNeeded: string[];
  verifiedStamp: { mode: string; note: string };
  message: string;
}

export type RequirementTier = 'electronic' | 'portal' | 'phone' | 'human';

export interface Requirement {
  id: string;
  label: string;
  target: {
    kind: 'plan' | 'maximum' | 'deductible' | 'category' | 'procedure';
    field?: string;
    category?: string;
    code?: string;
  };
  required: boolean;
  tier?: RequirementTier;
}

export interface VerificationTemplate {
  id: string;
  practiceId: string;
  name: string;
  appointmentTypeId: string;
  requirements: Requirement[];
  serviceTypeCodes: string[];
}

export type ImportField =
  | 'patientFirstName'
  | 'patientLastName'
  | 'patientDob'
  | 'appointmentType'
  | 'carrierName'
  | 'memberId'
  | 'appointmentDate'
  | 'appointmentTime'
  | 'providerName'
  | 'subscriberRelationship'
  | 'subscriberFirstName'
  | 'subscriberLastName'
  | 'subscriberDob'
  | 'groupNumber';

export type ColumnMapping = Partial<Record<ImportField, string | null>>;

export interface ImportFieldDef {
  key: ImportField;
  label: string;
  required: boolean;
}

export interface ImportFieldsResponse {
  fields: ImportFieldDef[];
  savedMapping: ColumnMapping | null;
  templateHeaders: string[];
}

// The parsed rows stay on the server; later import steps reference them by importId.
export interface ParseResult {
  importId: string;
  fileName: string;
  headers: string[];
  rowCount: number;
  expiresAt: string;
  mapping: ColumnMapping;
  missingRequired: string[];
}

export interface PayerDirectoryEntry {
  payerId: string;
  name: string;
  aliases: string[];
  mockSupported: boolean;
}

export interface CarrierResolution {
  carrierNameRaw: string;
  count: number;
  payerId: string | null;
  payerName: string | null;
  matchSource: 'saved' | 'suggested' | 'none';
  suggestions: PayerDirectoryEntry[];
}

export interface CarrierMappingInput {
  carrierNameRaw: string;
  payerId: string;
  payerName: string;
}

export interface PreviewRow {
  rowIndex: number;
  readiness: 'ready' | 'needs_fix' | 'needs_payer';
  missingFields: string[];
  message: string | null;
  duplicate: boolean;
  preview: {
    patientName: string;
    dobMasked: string | null;
    appointmentType: string;
    appointmentAt: string | null;
    carrier: string;
    memberIdMasked: string;
  };
}

export interface PreviewResult {
  rows: PreviewRow[];
  counts: {
    ready: number;
    needsFix: number;
    needsPayer: number;
    duplicates: number;
    total: number;
  };
}

export interface CommitResult {
  batchId: string;
  created: number;
  updated: number;
  /** Saved but flagged (missing details / no payer) — a subset of created + updated. */
  failed: number;
  /** Not saved: no patient name, duplicate within the file, or a save error. */
  skipped: number;
  checksQueued: number;
  total: number;
}

export interface ImportBatch {
  id: string;
  fileName: string;
  rowCount: number;
  createdCount: number;
  updatedCount: number;
  failedCount: number;
  /** Rows still attributed to this file — what deleting the import removes. */
  activeCount: number;
  createdAt: string;
}

export interface DeleteBatchResult {
  batchId: string;
  deletedVerifications: number;
  keptWrittenToPms: number;
}

export interface Connections {
  stedi: {
    configured: boolean;
    testMode: boolean;
    transport: 'http' | 'fixture';
    baseUrl: string;
  };
  openDental: {
    configured: boolean;
    writeMode: 'dry_run' | 'live';
    baseUrl: string;
  };
  /** Env fallback only — see providerIdentity for what a check actually sends. */
  provider: { npiConfigured: boolean; organizationName: string | null; fallbackOnly?: boolean };
  /** Per-practice provider identity for the requested location (null when no locationId was given). */
  providerIdentity:
    | {
        configured: true;
        source: 'open_dental' | 'location_credentials' | 'staff_credential' | 'env_fallback';
        isFallback: boolean;
        organizationName: string;
        npiLast4: string;
      }
    | { configured: false; message: string }
    | null;
  codeMap: {
    fetchedAt: string | null;
    covCat: Record<string, number>;
    codeGroupCount: number;
    covCatRows: number;
  };
  carrierMappings: number;
}

export interface CodeMapRefreshResult {
  covCat: Record<string, number>;
  codeGroupCount: number;
  covCatRows: number;
}

export interface MockDiagnosticResult {
  key: string;
  payerName: string;
  payerId: string;
  memberId: string;
  expected: 'benefits' | 'error';
  ok: boolean;
  transport: 'http' | 'fixture';
  durationMs: number;
  externalId: string | null;
  summary: string | null;
  error: { kind: string; message: string; aaa: string | null; technical: string } | null;
}

export interface StediDiagnostics {
  ranAt: string;
  config: Connections['stedi'];
  provider: Connections['providerIdentity'];
  organizationName: string;
  results: MockDiagnosticResult[];
  verdict: string;
}

export interface SyncResult {
  fetched: number;
  created: number;
  updated: number;
  skippedNoInsurance: number;
  checksQueued: number;
  sample: { aptNum: number; when: string; type: string; carrier: string | null }[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const BASE = '/insurance-verification';

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const verificationApi = {
  // Worklist
  listVerifications: (locationId: string, status?: VerificationStatus) =>
    apiClient
      .get<ApiResponse<WorklistRow[]>>(`${BASE}/verifications`, {
        params: { locationId, ...(status ? { status } : {}) },
      })
      .then((r) => r.data.data),

  getMetrics: (locationId: string) =>
    apiClient
      .get<ApiResponse<WorklistMetrics>>(`${BASE}/metrics`, { params: { locationId } })
      .then((r) => r.data.data),

  // Detail + actions
  getVerification: (id: string) =>
    apiClient
      .get<ApiResponse<VerificationDetail>>(`${BASE}/verifications/${id}`)
      .then((r) => r.data.data),

  approve: (id: string) =>
    apiClient
      .post<ApiResponse<ApproveResult>>(`${BASE}/verifications/${id}/approve`)
      .then((r) => r.data.data),

  flag: (id: string, note?: string) =>
    apiClient
      .post<ApiResponse<{ flagged: true }>>(`${BASE}/verifications/${id}/flag`, { note })
      .then((r) => r.data.data),

  queueCall: (id: string) =>
    apiClient
      .post<ApiResponse<{ questions: string[] }>>(`${BASE}/verifications/${id}/queue-call`)
      .then((r) => r.data.data),

  revealDob: (id: string) =>
    apiClient
      .post<ApiResponse<{ dateOfBirth: string | null }>>(`${BASE}/verifications/${id}/reveal-dob`)
      .then((r) => r.data.data),

  rerun: (id: string) =>
    apiClient
      .post<ApiResponse<{ queued: boolean; reason?: string }>>(`${BASE}/verifications/${id}/rerun`)
      .then((r) => r.data.data),

  // Templates
  listTemplates: () =>
    apiClient
      .get<ApiResponse<VerificationTemplate[]>>(`${BASE}/templates`)
      .then((r) => r.data.data),

  updateRequirement: (templateId: string, requirementId: string, required: boolean) =>
    apiClient
      .patch<ApiResponse<VerificationTemplate>>(`${BASE}/templates/${templateId}/requirements`, {
        requirementId,
        required,
      })
      .then((r) => r.data.data),

  // Import
  getImportFields: () =>
    apiClient
      .get<ApiResponse<ImportFieldsResponse>>(`${BASE}/import/fields`)
      .then((r) => r.data.data),

  downloadTemplateCsv: async () => {
    const r = await apiClient.get<Blob>(`${BASE}/import/template.csv`, { responseType: 'blob' });
    triggerDownload(r.data, 'schedule-template.csv');
  },

  downloadSampleCsv: async () => {
    const r = await apiClient.get<Blob>(`${BASE}/import/sample.csv`, { responseType: 'blob' });
    triggerDownload(r.data, 'schedule-sample.csv');
  },

  parseFile: (locationId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient
      .post<ApiResponse<ParseResult>>(`${BASE}/import/parse`, formData, {
        params: { locationId },
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data.data);
  },

  parseSample: (locationId: string) =>
    apiClient
      .post<ApiResponse<ParseResult>>(`${BASE}/import/parse-sample`, undefined, {
        params: { locationId },
      })
      .then((r) => r.data.data),

  saveMapping: (locationId: string, mapping: ColumnMapping) =>
    apiClient
      .put<ApiResponse<{ saved: true }>>(`${BASE}/import/mapping`, { locationId, mapping })
      .then((r) => r.data.data),

  resolveCarriers: (locationId: string, importId: string, mapping: ColumnMapping) =>
    apiClient
      .post<ApiResponse<CarrierResolution[]>>(`${BASE}/import/carriers`, {
        locationId,
        importId,
        mapping,
      })
      .then((r) => r.data.data),

  confirmCarriers: (locationId: string, mappings: CarrierMappingInput[]) =>
    apiClient
      .post<ApiResponse<{ saved: number }>>(`${BASE}/import/carriers/confirm`, {
        locationId,
        mappings,
      })
      .then((r) => r.data.data),

  searchPayers: (q: string) =>
    apiClient
      .get<ApiResponse<PayerDirectoryEntry[]>>(`${BASE}/payers/search`, { params: { q } })
      .then((r) => r.data.data),

  previewImport: (locationId: string, importId: string, mapping: ColumnMapping) =>
    apiClient
      .post<ApiResponse<PreviewResult>>(`${BASE}/import/preview`, { locationId, importId, mapping })
      .then((r) => r.data.data),

  commitImport: (locationId: string, importId: string, mapping: ColumnMapping) =>
    apiClient
      .post<ApiResponse<CommitResult>>(`${BASE}/import/commit`, {
        locationId,
        importId,
        mapping,
      })
      .then((r) => r.data.data),

  listBatches: (locationId: string) =>
    apiClient
      .get<ApiResponse<ImportBatch[]>>(`${BASE}/import/batches`, { params: { locationId } })
      .then((r) => r.data.data),

  // Connections
  getConnections: (locationId?: string) =>
    apiClient
      .get<
        ApiResponse<Connections>
      >(`${BASE}/connections`, { params: locationId ? { locationId } : undefined })
      .then((r) => r.data.data),

  refreshCodeMap: (locationId: string) =>
    apiClient
      .post<ApiResponse<CodeMapRefreshResult>>(`${BASE}/opendental/code-map/refresh`, {
        locationId,
      })
      .then((r) => r.data.data),

  runStediDiagnostics: (locationId: string, includeErrorMocks = true) =>
    apiClient
      .post<
        ApiResponse<StediDiagnostics>
      >(`${BASE}/stedi/diagnostics`, { locationId, includeErrorMocks })
      .then((r) => r.data.data),

  rerunFailed: (locationId: string) =>
    apiClient
      .post<
        ApiResponse<{ queued: number; skipped: number }>
      >(`${BASE}/verifications/rerun-failed`, { locationId })
      .then((r) => r.data.data),

  deleteVerification: (id: string) =>
    apiClient
      .delete<ApiResponse<{ deleted: true }>>(`${BASE}/verifications/${id}`)
      .then((r) => r.data.data),

  deleteBatch: (id: string) =>
    apiClient
      .delete<ApiResponse<DeleteBatchResult>>(`${BASE}/import/batches/${id}`)
      .then((r) => r.data.data),

  syncOpenDental: (locationId: string, days?: number) =>
    apiClient
      .post<ApiResponse<SyncResult>>(`${BASE}/opendental/sync`, { locationId, days })
      .then((r) => r.data.data),
};
